import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    useBusinessBuyer,
    useCustomerSelector,
    getShowroomWorkflowStatus,
    getCustomerVehiclesDealsCount,
    getDealRefId,
    isDealCommentsSVEnabled,
    isFeatureVisible,
    getDealPdfUrl,
    hasFinancialRecord,
    hasTradeIn,
    getActiveSectionKey,
    isFullPageScrollEnabled,
    useIsMissingVehicle
} from '@makemydeal/dr-sales-checkin-store';
import type { SalesCheckinRootState } from '@makemydeal/dr-sales-checkin-store';
import { formatCustomerName, useAppRouter } from '@makemydeal/dr-sales-checkin-ui-utils';
import { WorkflowStatus, routingConfig, type FeatureName } from '@makemydeal/dr-sales-checkin-types';
import { sectionKeyFromPathname } from '@makemydeal/dr-sales-checkin-ui-utils';

import { useNavigationHandler } from '../useNavigationHandler';
import type { NavViewContext } from '../../types/showroomNavTypes';

/**
 * Builds the {@link NavViewContext} once from live state/handlers. Runtime data is
 * read from its source slices — buyer/co-buyer names from `customer`, workflow /
 * deal-ref gating from `activeDeal`/`workflow`, the recent-deals count from
 * `customerVehicles` — rather than duplicated into the nav slice. Shared by the nav
 * hooks; memoized so downstream `useMemo`s stay stable.
 */
export const useNavViewModel = (): NavViewContext => {
    const dispatch = useDispatch();
    const { handlers } = useNavigationHandler();
    const { isBusinessBuyer, legalName } = useBusinessBuyer();
    const { checkedInCustomer, coBuyer } = useCustomerSelector();
    const dealRefId = useSelector(getDealRefId);
    const recentDealsCount = useSelector(getCustomerVehiclesDealsCount);
    const { pathname } = useAppRouter();
    const workflowStatus = useSelector(getShowroomWorkflowStatus);
    const isDealCommentsEnabled = useSelector(isDealCommentsSVEnabled);
    const isMissingVehicle = useIsMissingVehicle();
    const dealPdfUrl = useSelector(getDealPdfUrl);
    const hasFinancialRecordAvailable = useSelector(hasFinancialRecord);
    const hasTradeInOnDeal = useSelector(hasTradeIn);
    const isFullScrollEnabled = useSelector(isFullPageScrollEnabled);
    const scrolledSectionKey = useSelector(getActiveSectionKey);
    // The store's section key is page-session state: it survives leaving the page so the return
    // lands where the user left off (FR-029). The highlight has to answer a narrower question —
    // where is the user *now* — so it only trusts that key while they are actually on the page.
    // Off the page (Browse Inventory, or a former section address still served standalone) the
    // pathname decides, which means nothing is highlighted on a non-section destination.
    const isOnScrollPage = pathname === routingConfig.BUILD_YOUR_OFFER;
    const activeSectionKey = isFullScrollEnabled && isOnScrollPage ? scrolledSectionKey : sectionKeyFromPathname(pathname);

    const buyerLabel =
        isBusinessBuyer && legalName
            ? legalName
            : checkedInCustomer
            ? formatCustomerName(checkedInCustomer.firstName, checkedInCustomer.lastName)
            : '';
    const cobuyerLabel = coBuyer ? formatCustomerName(coBuyer.firstName, coBuyer.lastName) : '';
    const hasCobuyer = !!coBuyer;
    const hasDealRef = !!dealRefId;

    // Share-menu enablement (SCI rules — see PR #10218; not feature-toggle gated).

    // Credit App requires a financial record on the deal: currently desked/accepted, or
    // `hasFinancialRecord` (baggage published / manager-approved — see its doc comment).
    const isCreditAppEnabled =
        workflowStatus === WorkflowStatus.DESKED || workflowStatus === WorkflowStatus.ACCEPTED || hasFinancialRecordAvailable;

    // dealPdfUrl isn't cleared when the deal leaves DESKED (e.g. Request Changes), so it can
    // point at a stale PDF from a prior desk. Requiring DESKED here disables Deal PDF the
    // moment the deal is no longer desked, same as Credit App.
    const isPdfUploadEnabled =
        !!dealPdfUrl && (workflowStatus === WorkflowStatus.DESKED || workflowStatus === WorkflowStatus.ACCEPTED);

    const isCapabilityVisible = useSelector(
        (state: SalesCheckinRootState) => (feature: string) => isFeatureVisible(feature as FeatureName)(state)
    );

    return useMemo(
        () => ({
            dispatch,
            handlers,
            isBusinessBuyer,
            buyerLabel,
            cobuyerLabel,
            hasCobuyer,
            pathname,
            activeSectionKey,
            isMissingVehicle,
            isFullScrollEnabled,
            hasTradeIn: hasTradeInOnDeal,
            hasDealRef,
            recentDealsCount,
            isCreditAppEnabled,
            isPdfUploadEnabled,
            isDealCommentsEnabled,
            isCapabilityVisible
        }),
        [
            dispatch,
            handlers,
            isBusinessBuyer,
            buyerLabel,
            cobuyerLabel,
            hasCobuyer,
            pathname,
            activeSectionKey,
            isMissingVehicle,
            isFullScrollEnabled,
            hasTradeInOnDeal,
            hasDealRef,
            recentDealsCount,
            isCreditAppEnabled,
            isPdfUploadEnabled,
            isDealCommentsEnabled,
            isCapabilityVisible
        ]
    );
};
